import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useSystemSetting from '@/hooks/files/use-system-setting';
import { userInfoQueryOptions } from '@/hooks/users/use-user-info';
import { type MaybeResponseError, type RespondedType, ResponseError } from '@/models/response';
import { promiseLogger, SENTRY_LOG } from '@/services/sentry';
import { addToast, addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { readableFileSize } from '@/utils/file-size';
import { isRetryEnabledError, queryClient, RETRY_DELAYS } from '@/utils/query-client';

export default function useUploadUserAvatar() {
  const { t } = useTranslation();
  const { getFileSizeLimit, isLoading: isSystemSettingLoading } = useSystemSetting();

  const mutation = useMutation<RespondedType, MaybeResponseError, File>({
    mutationKey: ['/UploadHeadImg'],
    mutationFn: (params) =>
      fetcher(
        '/UploadHeadImg',
        { file: params },
        {
          requestContentType: 'form-data',
          validResponseCodes: [0],
          authPlacement: 'Data',
          target: 'file-upload',
        },
      ),
    onSuccess: async (data) => {
      await queryClient.refetchQueries(userInfoQueryOptions);

      addToast({
        type: 'success',
        message: data.Msg,
      });
    },
    retry: (failureCount, error) => isRetryEnabledError(error) && failureCount <= 5,
    retryDelay: (failureCount) => RETRY_DELAYS[failureCount] ?? 10000,
    onError: (error) => {
      if (ResponseError.isResponseError(error)) {
        addToast({
          type: 'error',
          message: error.message,
        });
      } else {
        addTranslateToast({
          type: 'error',
          message: t('upload.failed') + t('common.try-again-later'),
        });
      }
      console.error(error);
    },
  });

  async function upload(file: File) {
    const limit = getFileSizeLimit('head');

    if (file.size >= limit) {
      addToast({
        type: 'error',
        message: t('errors.file-size-limit', { limit: readableFileSize(limit) }),
      });
      return Promise.reject('FILE_TOO_LARGE');
    }

    mutation.reset();
    return promiseLogger({
      name: SENTRY_LOG.UPLOAD,
      action: () => mutation.mutateAsync(file),
      requestAttributes: {
        'file-size': `${file.size}`,
        'file-type': file.type,
      },
    });
  }

  return {
    upload,
    isLoading: isSystemSettingLoading || mutation.isPending,
    ...mutation,
  };
}
